import { observer } from 'mobx-react';
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@components/ui/table';
import { Spinner } from '../../../components/spinner';
import { PushTokenValue } from './push-token-value';
import {
    UserDeviceSupportDto,
    UserDevicesOverview,
    UserPushTokenSupportDto,
    useUserDevicesStore
} from './store';

const formatDateTime = (value?: string) =>
    value ? new Date(value).toLocaleString() : '-';

const deviceTitle = (device: UserDeviceSupportDto) =>
    [device.manufacturer, device.name ?? device.model]
        .filter(Boolean)
        .join(' ') || 'Unknown device';

const deviceDescription = (device: UserDeviceSupportDto) =>
    [
        device.model,
        device.osVersion &&
            `${device.platform === 'IOS' ? 'iOS' : 'Android'} ${device.osVersion}`,
        device.locale
    ]
        .filter(Boolean)
        .join(' · ');

const PushTokenBadge = ({
    type
}: {
    type: UserPushTokenSupportDto['type'];
}) => <Badge variant={type === 'EXPO' ? 'info' : 'muted'}>{type}</Badge>;

const DeviceCard = ({
    device,
    pushTokens
}: {
    device: UserDeviceSupportDto;
    pushTokens: UserPushTokenSupportDto[];
}) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                {deviceTitle(device)}
                {device.platform && (
                    <Badge variant="outline">{device.platform}</Badge>
                )}
            </CardTitle>
            <CardDescription>
                {deviceDescription(device)}
                {' · first seen '}
                {formatDateTime(device.createdDate)}
                {' · updated '}
                {formatDateTime(device.modifiedDate)}
            </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>App</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Push token</TableHead>
                        <TableHead>Last confirmed</TableHead>
                        <TableHead>Installed</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {device.appInstallations.map((installation) => {
                        const installationTokens = pushTokens.filter(
                            (pushToken) =>
                                pushToken.appInstallationId === installation.id
                        );

                        return (
                            <TableRow key={installation.id}>
                                <TableCell>
                                    {installation.appId || '-'}
                                </TableCell>
                                <TableCell>
                                    {installation.appVersion || '-'}
                                </TableCell>
                                <TableCell>
                                    {installationTokens.length === 0
                                        ? '-'
                                        : installationTokens.map(
                                              (pushToken) => (
                                                  <div
                                                      key={pushToken.id}
                                                      className="flex items-center gap-2"
                                                  >
                                                      <PushTokenBadge
                                                          type={pushToken.type}
                                                      />
                                                      <PushTokenValue
                                                          token={
                                                              pushToken.token
                                                          }
                                                      />
                                                  </div>
                                              )
                                          )}
                                </TableCell>
                                <TableCell>
                                    {installationTokens
                                        .map((pushToken) =>
                                            formatDateTime(
                                                pushToken.lastConfirmedDate
                                            )
                                        )
                                        .join(', ') || '-'}
                                </TableCell>
                                <TableCell>
                                    {formatDateTime(installation.createdDate)}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </CardContent>
    </Card>
);

const PushTokensWithoutDevice = ({
    pushTokens
}: {
    pushTokens: UserPushTokenSupportDto[];
}) => (
    <Card>
        <CardHeader>
            <CardTitle>Push tokens without a device</CardTitle>
            <CardDescription>
                Registered by an app that sends no device information, such as
                the previous Move Republic app.
            </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>Push token</TableHead>
                        <TableHead>Time zone</TableHead>
                        <TableHead>Last confirmed</TableHead>
                        <TableHead>Registered</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {pushTokens.map((pushToken) => (
                        <TableRow key={pushToken.id}>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <PushTokenBadge type={pushToken.type} />
                                    <PushTokenValue token={pushToken.token} />
                                </div>
                            </TableCell>
                            <TableCell>{pushToken.timeZone || '-'}</TableCell>
                            <TableCell>
                                {formatDateTime(pushToken.lastConfirmedDate)}
                            </TableCell>
                            <TableCell>
                                {formatDateTime(pushToken.createdDate)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </CardContent>
    </Card>
);

const DevicesOverview = ({ devices, pushTokens }: UserDevicesOverview) => {
    const pushTokensWithoutDevice = pushTokens.filter(
        (pushToken) => pushToken.appInstallationId === undefined
    );

    if (devices.length === 0 && pushTokensWithoutDevice.length === 0) {
        return (
            <Alert variant="info">
                <AlertDescription>
                    This user has no registered devices or push tokens.
                </AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {devices.map((device) => (
                <DeviceCard
                    key={device.id}
                    device={device}
                    pushTokens={pushTokens}
                />
            ))}
            {pushTokensWithoutDevice.length > 0 && (
                <PushTokensWithoutDevice pushTokens={pushTokensWithoutDevice} />
            )}
        </div>
    );
};

export const UserDevices = observer(() => {
    const { overview } = useUserDevicesStore();

    return (
        <div>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">
                Devices
            </h2>

            {overview.match({
                loading: () => (
                    <div className="flex justify-center py-12">
                        <Spinner />
                    </div>
                ),
                error: (error) => (
                    <Alert variant="destructive">
                        <AlertTitle>Error loading devices</AlertTitle>
                        <AlertDescription>{error.message}</AlertDescription>
                    </Alert>
                ),
                fetched: (result) => <DevicesOverview {...result} />
            })}
        </div>
    );
});

export default UserDevices;
